import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
    getBRSDetails,
    finalizeBRS,
    clearBRSEntries,
    autoMatchBRS,
    manualMatchBRS,
    unmatchBRS,
    importBRSStatement,
} from '../../../../redux/actions/SocietyPortal/BRSAction/BRSAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ACCOUNTS_RECONCILIATION } from '../../../../Routing/routes';

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d)) return date;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${d.getFullYear()}`;
};

const BRSDetail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { brsId } = useParams();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        details,
        detailsLoading,
        finalizeLoading,
        autoMatchLoading,
        clearLoading,
        manualMatchLoading,
        unmatchLoading,
        importLoading,
    } = useSelector(state => state.BRSReducer || {});

    const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
    const [selectedStatementId, setSelectedStatementId] = useState(null);
    const [selectedToClear, setSelectedToClear] = useState({}); // { [journalId]: clearingDate }
    const [showImportModal, setShowImportModal] = useState(false);

    const fetchDetails = () => {
        if (societyId && brsId) {
            dispatch(getBRSDetails({ societyId, brsId }));
        }
    };

    useEffect(() => {
        fetchDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [societyId, brsId, dispatch]);

    const snapshots = details?.snapshotLogs || [];
    const statements = details?.bankStatements || [];
    const isFinalized = details?.status === 'finalized';

    const unmatchedSnapshots = useMemo(() => snapshots.filter(s => !s.is_cleared), [snapshots]);
    const matchedSnapshots = useMemo(() => snapshots.filter(s => s.is_cleared), [snapshots]);
    const unmatchedStatements = useMemo(() => statements.filter(s => !s.is_mapped), [statements]);
    const matchedStatements = useMemo(() => statements.filter(s => s.is_mapped), [statements]);

    const difference = useMemo(() => {
        const book = parseFloat(details?.book_balance) || 0;
        const bank = parseFloat(details?.bank_balance) || 0;
        return book - bank;
    }, [details]);

    const goBack = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_RECONCILIATION);

    const handleAutoMatch = () => {
        if (isFinalized) return;
        dispatch(autoMatchBRS({
            societyId,
            brsId,
            onSuccess: () => fetchDetails(),
        }));
    };

    const handleManualMatch = () => {
        if (!selectedSnapshotId || !selectedStatementId) {
            toast.warn('Select one book entry and one bank statement row');
            return;
        }
        dispatch(manualMatchBRS({
            societyId,
            brsId,
            snapshot_id: selectedSnapshotId,
            statement_id: selectedStatementId,
            onSuccess: () => {
                setSelectedSnapshotId(null);
                setSelectedStatementId(null);
                fetchDetails();
            },
        }));
    };

    const handleUnmatch = (snapshotId) => {
        dispatch(unmatchBRS({
            societyId,
            brsId,
            snapshot_id: snapshotId,
            onSuccess: () => fetchDetails(),
        }));
    };

    const handleToggleClear = (journalId) => {
        setSelectedToClear(prev => {
            const next = { ...prev };
            if (next[journalId]) delete next[journalId];
            else next[journalId] = localDateString();
            return next;
        });
    };

    const handleClearDateChange = (journalId, date) => {
        setSelectedToClear(prev => ({ ...prev, [journalId]: date }));
    };

    const handleClearSelected = () => {
        const entries = Object.entries(selectedToClear).map(([journal_id, clearing_date]) => ({
            journal_id: Number(journal_id),
            clearing_date,
        }));
        if (entries.length === 0) {
            toast.warn('Select at least one entry to clear');
            return;
        }
        dispatch(clearBRSEntries({
            societyId,
            brsId,
            entries,
            onSuccess: () => {
                setSelectedToClear({});
                fetchDetails();
            },
        }));
    };

    const handleFinalize = () => {
        if (!window.confirm('Finalize this BRS? You will not be able to make changes after this.')) return;
        dispatch(finalizeBRS({
            societyId,
            brsId,
            onSuccess: () => fetchDetails(),
        }));
    };

    if (detailsLoading && !details) {
        return (
            <div className="tw-p-5 tw-flex tw-items-center tw-justify-center tw-min-h-screen">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                    <span className="tw-text-xs tw-text-gray-400">Loading BRS...</span>
                </div>
            </div>
        );
    }

    if (!details) {
        return (
            <div className="tw-p-5">
                <button onClick={goBack} className="tw-text-xs tw-text-gray-500 hover:tw-text-gray-700 tw-flex tw-items-center tw-gap-1">
                    <Icon icon="mdi:arrow-left" /> Back to BRS List
                </button>
                <p className="tw-text-center tw-text-gray-400 tw-py-16 tw-text-xs">BRS not found</p>
            </div>
        );
    }

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            {/* Header */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button onClick={goBack} className="tw-p-1.5 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500">
                            <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                        </button>
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="hugeicons:structure-03" className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h1 className="tw-text-base tw-font-bold tw-text-gray-800">
                                BRS — {details.account_cd}
                                <span className={`tw-ml-2 tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-capitalize tw-border ${isFinalized ? 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-100' : 'tw-bg-amber-50 tw-text-amber-600 tw-border-amber-100'}`}>
                                    {details.status}
                                </span>
                            </h1>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                Period: {formatDate(details.start_date)} – {formatDate(details.end_date)} · FY {details.fin_year}
                            </p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={fetchDetails}
                            className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500"
                            title="Refresh"
                        >
                            <Icon icon="mdi:refresh" className="tw-text-lg" />
                        </button>
                        {!isFinalized && (
                            <>
                                <button
                                    onClick={() => setShowImportModal(true)}
                                    className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700"
                                >
                                    <Icon icon="mdi:upload" className="tw-text-sm" /> Import Statement
                                </button>
                                <button
                                    onClick={handleAutoMatch}
                                    disabled={autoMatchLoading}
                                    className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-purple-600 hover:tw-bg-purple-700 disabled:tw-opacity-50"
                                >
                                    {autoMatchLoading
                                        ? <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>
                                        : <Icon icon="mdi:auto-fix" className="tw-text-sm" />}
                                    Auto-Match
                                </button>
                                <button
                                    onClick={handleFinalize}
                                    disabled={finalizeLoading}
                                    className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-emerald-600 hover:tw-bg-emerald-700 disabled:tw-opacity-50"
                                >
                                    {finalizeLoading
                                        ? <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>
                                        : <Icon icon="mdi:lock-check" className="tw-text-sm" />}
                                    Finalize
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Summary cards */}
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-2.5">
                <div className="tw-bg-white tw-border tw-border-rose-100 tw-rounded-lg tw-p-3">
                    <p className="tw-text-[10px] tw-text-rose-600 tw-uppercase tw-font-semibold">Book Balance</p>
                    <p className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-1">₹{formatCurrency(details.book_balance)}</p>
                </div>
                <div className="tw-bg-white tw-border tw-border-blue-100 tw-rounded-lg tw-p-3">
                    <p className="tw-text-[10px] tw-text-blue-600 tw-uppercase tw-font-semibold">Bank Balance</p>
                    <p className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-1">₹{formatCurrency(details.bank_balance)}</p>
                </div>
                <div className={`tw-bg-white tw-border ${Math.abs(difference) < 0.01 ? 'tw-border-emerald-100' : 'tw-border-amber-100'} tw-rounded-lg tw-p-3`}>
                    <p className={`tw-text-[10px] tw-uppercase tw-font-semibold ${Math.abs(difference) < 0.01 ? 'tw-text-emerald-600' : 'tw-text-amber-600'}`}>Difference</p>
                    <p className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-1">₹{formatCurrency(Math.abs(difference))}</p>
                </div>
                <div className="tw-bg-white tw-border tw-border-purple-100 tw-rounded-lg tw-p-3">
                    <p className="tw-text-[10px] tw-text-purple-600 tw-uppercase tw-font-semibold">Matched / Total</p>
                    <p className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-1">
                        {matchedSnapshots.length + matchedStatements.length} / {snapshots.length + statements.length}
                    </p>
                </div>
            </div>

            {/* Manual Match Bar */}
            {!isFinalized && (selectedSnapshotId || selectedStatementId) && (
                <div className="tw-bg-purple-50 tw-border tw-border-purple-200 tw-rounded-lg tw-p-3 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-text-xs tw-text-purple-700">
                        <span className="tw-font-medium">Selected:</span>
                        {' '}Book #{selectedSnapshotId || '—'}
                        {' · '}Bank #{selectedStatementId || '—'}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={() => { setSelectedSnapshotId(null); setSelectedStatementId(null); }}
                            className="tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200"
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleManualMatch}
                            disabled={!selectedSnapshotId || !selectedStatementId || manualMatchLoading}
                            className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-text-white tw-bg-purple-600 hover:tw-bg-purple-700 disabled:tw-opacity-50"
                        >
                            {manualMatchLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>}
                            <Icon icon="mdi:link-variant" /> Link Entries
                        </button>
                    </div>
                </div>
            )}

            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
                {/* Book Snapshot */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    <div className="tw-p-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                        <div>
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Book Entries</h3>
                            <p className="tw-text-[10px] tw-text-gray-500">{snapshots.length} entries · {unmatchedSnapshots.length} pending</p>
                        </div>
                        {!isFinalized && Object.keys(selectedToClear).length > 0 && (
                            <button
                                onClick={handleClearSelected}
                                disabled={clearLoading}
                                className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-text-white tw-bg-emerald-600 hover:tw-bg-emerald-700 disabled:tw-opacity-50"
                            >
                                {clearLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>}
                                Clear {Object.keys(selectedToClear).length}
                            </button>
                        )}
                    </div>
                    <div className="tw-overflow-x-auto tw-max-h-[500px] tw-overflow-y-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead className="tw-sticky tw-top-0 tw-bg-white tw-z-10">
                                <tr className="tw-bg-gray-50">
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Date</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Narration</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Cheque / Side</th>
                                    <th className="tw-text-right tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Amount</th>
                                    <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Action</th>
                                </tr>
                            </thead>
                            <tbody className="tw-divide-y tw-divide-gray-50">
                                {snapshots.length > 0 ? snapshots.map(row => {
                                    const isSelected = selectedSnapshotId === row.id;
                                    const isChecked = !!selectedToClear[row.journal_id];
                                    return (
                                        <tr
                                            key={row.id}
                                            className={`${isSelected ? 'tw-bg-purple-50/70' : 'hover:tw-bg-slate-50/50'} ${row.is_cleared ? 'tw-opacity-60' : ''}`}
                                            onClick={() => !isFinalized && !row.is_cleared && setSelectedSnapshotId(isSelected ? null : row.id)}
                                            style={{ cursor: !isFinalized && !row.is_cleared ? 'pointer' : 'default' }}
                                        >
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-whitespace-nowrap">{formatDate(row.transaction_date)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-700">{row.narration || '—'}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-500">
                                                {row.cheque_no || '—'}
                                                <span className={`tw-ml-1 tw-text-[9px] tw-uppercase tw-font-semibold ${row.side === 'debit' ? 'tw-text-emerald-600' : 'tw-text-rose-600'}`}>{row.side}</span>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-gray-700">₹{formatCurrency(row.amount)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-center">
                                                {row.is_cleared ? (
                                                    !isFinalized ? (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleUnmatch(row.id); }}
                                                            disabled={unmatchLoading}
                                                            className="tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-bg-red-50 tw-text-red-500 hover:tw-bg-red-100"
                                                        >
                                                            Unmatch
                                                        </button>
                                                    ) : <span className="tw-text-[10px] tw-text-emerald-600">Matched</span>
                                                ) : !isFinalized ? (
                                                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-1" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => handleToggleClear(row.journal_id)}
                                                            className="tw-w-3 tw-h-3"
                                                            title="Mark as cleared"
                                                        />
                                                        {isChecked && (
                                                            <input
                                                                type="date"
                                                                value={selectedToClear[row.journal_id]}
                                                                onChange={(e) => handleClearDateChange(row.journal_id, e.target.value)}
                                                                className="tw-text-[10px] tw-px-1 tw-py-0.5 tw-border tw-border-gray-200 tw-rounded"
                                                            />
                                                        )}
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan={5} className="tw-text-center tw-py-8 tw-text-xs tw-text-gray-400">No book entries</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Bank Statement */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    <div className="tw-p-3 tw-border-b tw-border-gray-100">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Bank Statement</h3>
                        <p className="tw-text-[10px] tw-text-gray-500">{statements.length} entries · {unmatchedStatements.length} unmatched</p>
                    </div>
                    <div className="tw-overflow-x-auto tw-max-h-[500px] tw-overflow-y-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead className="tw-sticky tw-top-0 tw-bg-white tw-z-10">
                                <tr className="tw-bg-gray-50">
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Date</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Ref / Description</th>
                                    <th className="tw-text-right tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Debit</th>
                                    <th className="tw-text-right tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Credit</th>
                                    <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Status</th>
                                </tr>
                            </thead>
                            <tbody className="tw-divide-y tw-divide-gray-50">
                                {statements.length > 0 ? statements.map(row => {
                                    const isSelected = selectedStatementId === row.id;
                                    return (
                                        <tr
                                            key={row.id}
                                            className={`${isSelected ? 'tw-bg-purple-50/70' : 'hover:tw-bg-slate-50/50'} ${row.is_mapped ? 'tw-opacity-60' : ''}`}
                                            onClick={() => !isFinalized && !row.is_mapped && setSelectedStatementId(isSelected ? null : row.id)}
                                            style={{ cursor: !isFinalized && !row.is_mapped ? 'pointer' : 'default' }}
                                        >
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-whitespace-nowrap">{formatDate(row.trans_date)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-700">
                                                <div className="tw-font-medium tw-text-gray-700">{row.ref_no || '—'}</div>
                                                <div className="tw-text-[10px] tw-text-gray-500">{row.description || ''}</div>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right">
                                                {parseFloat(row.debit) > 0
                                                    ? <span className="tw-text-emerald-600">₹{formatCurrency(row.debit)}</span>
                                                    : <span className="tw-text-gray-300">—</span>}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right">
                                                {parseFloat(row.credit) > 0
                                                    ? <span className="tw-text-rose-600">₹{formatCurrency(row.credit)}</span>
                                                    : <span className="tw-text-gray-300">—</span>}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-center">
                                                {row.is_mapped
                                                    ? <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-bg-emerald-50 tw-text-emerald-600">Matched</span>
                                                    : <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-bg-amber-50 tw-text-amber-600">Unmatched</span>}
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan={5} className="tw-text-center tw-py-8 tw-text-xs tw-text-gray-400">
                                        No bank statement rows. Click <strong>Import Statement</strong> to add.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {showImportModal && (
                <ImportStatementModal
                    onClose={() => setShowImportModal(false)}
                    onSubmit={(rows) => {
                        dispatch(importBRSStatement({
                            societyId,
                            brsId,
                            rows,
                            onSuccess: () => {
                                setShowImportModal(false);
                                fetchDetails();
                            },
                        }));
                    }}
                    loading={importLoading}
                />
            )}
        </div>
    );
};

// --- Simple inline Import Statement Modal ---
const emptyRow = () => ({
    id: Date.now() + Math.random(),
    trans_date: localDateString(),
    ref_no: '',
    description: '',
    debit: '',
    credit: '',
    balance: '',
});

const ImportStatementModal = ({ onClose, onSubmit, loading }) => {
    const [rows, setRows] = useState([emptyRow()]);

    const update = (idx, field, value) => {
        setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };

    const addRow = () => setRows(prev => [...prev, emptyRow()]);
    const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

    const handleSubmit = () => {
        const payload = rows
            .filter(r => r.trans_date && (parseFloat(r.debit) > 0 || parseFloat(r.credit) > 0))
            .map(r => ({
                trans_date: r.trans_date,
                ref_no: r.ref_no || undefined,
                description: r.description || undefined,
                debit: parseFloat(r.debit) || 0,
                credit: parseFloat(r.credit) || 0,
                balance: parseFloat(r.balance) || 0,
            }));
        if (payload.length === 0) {
            toast.warn('Add at least one valid row');
            return;
        }
        onSubmit?.(payload);
    };

    const inputClass = 'tw-w-full tw-px-2 tw-py-1 tw-text-xs tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-border-rose-500';

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40" onClick={onClose}>
            <div
                className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-4xl tw-mx-4 tw-max-h-[92vh] tw-overflow-hidden tw-flex tw-flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-600 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:upload" className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Import Bank Statement</h2>
                            <p className="tw-text-[11px] tw-text-gray-500">Manually enter or paste rows from your bank statement</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                <div className="tw-flex-1 tw-overflow-y-auto tw-p-5">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50">
                                <th className="tw-text-left tw-px-2 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Date</th>
                                <th className="tw-text-left tw-px-2 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Ref / UTR</th>
                                <th className="tw-text-left tw-px-2 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Description</th>
                                <th className="tw-text-right tw-px-2 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Debit</th>
                                <th className="tw-text-right tw-px-2 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Credit</th>
                                <th className="tw-text-right tw-px-2 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[10px]">Balance</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={r.id} className="tw-border-b tw-border-gray-50">
                                    <td className="tw-px-2 tw-py-1"><input type="date" value={r.trans_date} onChange={(e) => update(idx, 'trans_date', e.target.value)} className={inputClass} /></td>
                                    <td className="tw-px-2 tw-py-1"><input type="text" value={r.ref_no} onChange={(e) => update(idx, 'ref_no', e.target.value)} className={inputClass} placeholder="UTR/CHQ" /></td>
                                    <td className="tw-px-2 tw-py-1"><input type="text" value={r.description} onChange={(e) => update(idx, 'description', e.target.value)} className={inputClass} placeholder="Narration" /></td>
                                    <td className="tw-px-2 tw-py-1"><input type="number" step="0.01" value={r.debit} onChange={(e) => update(idx, 'debit', e.target.value)} className={`${inputClass} tw-text-right`} placeholder="0.00" /></td>
                                    <td className="tw-px-2 tw-py-1"><input type="number" step="0.01" value={r.credit} onChange={(e) => update(idx, 'credit', e.target.value)} className={`${inputClass} tw-text-right`} placeholder="0.00" /></td>
                                    <td className="tw-px-2 tw-py-1"><input type="number" step="0.01" value={r.balance} onChange={(e) => update(idx, 'balance', e.target.value)} className={`${inputClass} tw-text-right`} placeholder="0.00" /></td>
                                    <td className="tw-px-2 tw-py-1">
                                        <button
                                            onClick={() => removeRow(idx)}
                                            disabled={rows.length === 1}
                                            className="tw-p-1 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500 disabled:tw-opacity-30"
                                        >
                                            <Icon icon="mdi:trash-can-outline" className="tw-text-sm" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button onClick={addRow} className="tw-mt-2 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium">
                        <Icon icon="mdi:plus-circle-outline" /> Add Row
                    </button>
                </div>

                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50">
                    <button onClick={onClose} disabled={loading} className="tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50">Cancel</button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 disabled:tw-opacity-50"
                    >
                        {loading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>}
                        <Icon icon="mdi:upload" /> Import
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BRSDetail;
